import {
  isDefined,
  isNotDefined,
  messageFromError,
  type NotificationItem,
  type NotificationListInput,
  type NotificationListOutput,
  type NotificationTopic,
  type NotificationView,
} from '@terros/common'
import { useInfiniteQuery, useIsMutating } from '@tanstack/react-query'
import { type GetInfiniteHookResponse } from '../infiniteQuery'
import { useServerRequest } from '../api'

export type NotificationListFilter = { view: NotificationView; topic?: NotificationTopic }

export function nextNotificationPage(
  input: NotificationListInput,
  page: NotificationListOutput
): NotificationListInput | undefined {
  return isDefined(page.nextCursor) ? { ...input, cursor: page.nextCursor } : undefined
}

export function useNotificationList(
  filter: NotificationListFilter,
  { enabled = true }: { enabled?: boolean } = {}
): GetInfiniteHookResponse<NotificationItem> {
  const request = useServerRequest<NotificationListInput, NotificationListOutput>('/notification/list')
  const input: NotificationListInput = { ...filter, limit: 20 }
  const pending = useIsMutating({ mutationKey: ['notification-center', 'read-update'] }) > 0
  const query = useInfiniteQuery({
    queryKey: ['notification-center', 'list', filter],
    initialPageParam: input,
    queryFn: ({ pageParam }) => request(pageParam),
    getNextPageParam: (page) => nextNotificationPage(input, page),
    enabled: enabled && !pending,
    retry: false,
    staleTime: 0,
    refetchInterval: 45000,
    refetchOnWindowFocus: true,
  })
  return {
    ...query,
    data:
      query.data?.pages
        .flatMap((page) => page.items)
        .filter((item) => filter.view !== 'unread' || isNotDefined(item.state.readAt)) ?? [],
    error: messageFromError(query.error),
  }
}
